import { instance } from "api/apiBase";

export interface NylasAuthUrlRequest {
  product: "calendar" | "mail";
  provider?: "google" | "microsoft";
  display_name?: string;
  is_default?: boolean;
  account_type?: "personal" | "shared";
  login_hint?: string;
}

interface NylasAuthUrlResponse {
  auth_url: string;
}

export interface NylasCalendar {
  id: number;
  calendar_id: string;
  name: string;
  enabled: boolean;
}

export interface NylasGrantConnectedBy {
  id: number;
  name: string;
}

export interface NylasGrantPropertyAccess {
  type: "all" | "specific";
  property_ids: number[];
}

export interface NylasGrantAssignedProperty {
  id: number;
  name: string;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state_or_province: string | null;
  postal_code: string | null;
}

export interface NylasGrant {
  id: number;
  nylas_grant_id: string;
  provider: "google" | "microsoft";
  email: string;
  product: "calendar" | "mail";
  account_type: "personal" | "shared";
  status?: "active" | "expired";
  partner_user_id: number;
  connected_by: NylasGrantConnectedBy | null;
  property_access: NylasGrantPropertyAccess;
  can_edit_property_access: boolean;
  can_disconnect: boolean;
  assigned_properties?: NylasGrantAssignedProperty[];
  sync_enabled: boolean;
  boom_calendar_id: string | null;
  calendar_setup_complete: boolean;
  needs_reauth?: boolean;
  created_at: string;
  last_synced_at: string | null;
  calendars?: NylasCalendar[];
}

interface NylasSyncSettingsUser {
  id: number;
  name: string;
  email: string;
}

export interface NylasSyncSettings {
  self_user_id: number;
  selected_user_ids: number[];
  available_users: NylasSyncSettingsUser[];
}

export const nylasApi = {
  generateAuthUrl: async (
    data: NylasAuthUrlRequest
  ): Promise<NylasAuthUrlResponse> => {
    return (await instance.post("/portal/v1/nylas/auth/url", data)).data;
  },

  listGrants: async (params?: { product?: string }): Promise<NylasGrant[]> => {
    return (await instance.get("/portal/v1/nylas/grants", { params })).data;
  },

  listAvailableMailGrants: async (): Promise<NylasGrant[]> => {
    return (await instance.get("/portal/v1/nylas/grants/available")).data;
  },

  disconnectGrant: async (grantId: number): Promise<void> => {
    await instance.delete(`/portal/v1/nylas/grants/${grantId}`);
  },

  updateGrant: async (
    grantId: number,
    data: { sync_enabled?: boolean }
  ): Promise<NylasGrant> => {
    return (await instance.put(`/portal/v1/nylas/grants/${grantId}`, data))
      .data;
  },

  listCalendars: async (grantId: number): Promise<NylasCalendar[]> => {
    return (await instance.get(`/portal/v1/nylas/grants/${grantId}/calendars`))
      .data;
  },

  updatePropertyAccess: async (
    grantId: number,
    propertyIds: number[]
  ): Promise<NylasGrant> => {
    return (
      await instance.put(`/portal/v1/nylas/grants/${grantId}/property_access`, {
        property_ids: propertyIds
      })
    ).data;
  },

  updateCalendars: async (
    grantId: number,
    selections: { calendar_id: string; enabled: boolean }[]
  ): Promise<NylasCalendar[]> => {
    return (
      await instance.put(`/portal/v1/nylas/grants/${grantId}/calendars`, {
        selections
      })
    ).data;
  },

  getSyncSettings: async (): Promise<NylasSyncSettings> => {
    return (await instance.get("/portal/v1/nylas/sync_settings")).data;
  },

  updateSyncSettings: async (
    sourceUserIds: number[]
  ): Promise<NylasSyncSettings> => {
    return (
      await instance.put("/portal/v1/nylas/sync_settings", {
        source_user_ids: sourceUserIds
      })
    ).data;
  },

  getSyncCooldown: async (): Promise<{ cooldown_seconds: number }> => {
    return (await instance.get("/portal/v1/nylas/grants/sync_cooldown")).data;
  },

  syncCalendars: async (): Promise<{
    cooldown_seconds: number;
    grants: NylasGrant[];
  }> => {
    return (await instance.post("/portal/v1/nylas/grants/sync_calendars")).data;
  },

  retryCalendar: async (grantId: number): Promise<NylasGrant> => {
    return (
      await instance.post(`/portal/v1/nylas/grants/${grantId}/retry_calendar`)
    ).data;
  }
};
